import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import './FlyerAnalysis.css';
import { getCountryName, sortCountries, ALL_COUNTRIES } from './countries';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

export default function FlyerAnalysisDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();

  const [countries, setCountries] = useState(ALL_COUNTRIES);
  const [country, setCountry] = useState(() => {
    return searchParams.get('country') || localStorage.getItem('fa_country') || '';
  });
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCountries = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get(`${API_BASE}/api/flyer-analysis/s3/countries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCountries(sortCountries(res.data.countries || []));
      // Set default country if none selected
      if (!country && res.data.countries?.length > 0) {
        const defaultCountry = res.data.countries[0];
        setCountry(defaultCountry);
        localStorage.setItem('fa_country', defaultCountry);
        setSearchParams({ country: defaultCountry });
      }
    } catch (err) {
      console.error('Error fetching countries:', err);
      setLoading(false);
    }
  }, [getAccessTokenSilently, country, setSearchParams]);

  const fetchData = useCallback(async () => {
    if (!country) { setLoading(false); return; }
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, jobsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/flyer-analysis/stats/${country}`, { headers }),
        axios.get(`${API_BASE}/api/flyer-analysis/jobs?country=${country}&limit=10`, { headers }),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data.jobs || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, country]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    if (country) fetchData();
  }, [country, fetchData]);

  const handleCountryChange = (e) => {
    const val = e.target.value;
    setCountry(val);
    localStorage.setItem('fa_country', val);
    setSearchParams({ country: val });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const handleResumeJob = async (jobId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${jobId}/resume`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification('Job resumed', 'info');
      fetchData();
    } catch (err) {
      console.error('Error resuming job:', err);
      showNotification('Error resuming job', 'error');
    }
  };

  const handlePauseJob = async (jobId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${jobId}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification('Job paused', 'info');
      fetchData();
    } catch (err) {
      console.error('Error pausing job:', err);
      showNotification('Error pausing job', 'error');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.post(`${API_BASE}/api/flyer-analysis/jobs/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification('Job cancelled', 'info');
      fetchData();
    } catch (err) {
      console.error('Error cancelling job:', err);
      showNotification('Error cancelling job', 'error');
    }
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <div className="main-column" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="fa-header">
        <div className="fa-header-left">
          <div>
            <h1 className="fa-header-title">Flyer Analysis</h1>
            <p className="fa-header-subtitle">AI-powered flyer analysis and insights</p>
          </div>
        </div>
        <div className="fa-header-actions">
          <select className="fa-country-select" value={country} onChange={handleCountryChange}>
            <option value="" disabled>Select Country</option>
            {countries.map(c => (
              <option key={c} value={c}>{getCountryName(c)}</option>
            ))}
          </select>
          <button className="fa-btn primary" onClick={() => navigate(`/flyer-analysis/new-job?country=${country}`)}>
            + New Job
          </button>
        </div>
      </div>

      {/* Sub-navigation */}
      <div className="fa-subnav">
        <a href="/flyer-analysis" className="fa-subnav-link active">Dashboard</a>
        <a href="/flyer-analysis/new-job" className="fa-subnav-link">New Job</a>
        <a href={`/flyer-analysis/browse?country=${country}`} className="fa-subnav-link">Browse Flyers</a>
        <a href={`/flyer-analysis/insights?country=${country}`} className="fa-subnav-link">Insights</a>
      </div>

      {/* Content */}
      <div className="fa-scroll-content">
        {loading ? (
          <div className="fa-loading">Loading...</div>
        ) : (
          <>
            {/* Stats Cards */}
            {stats && (
              <div className="fa-stats-grid">
                <div className="fa-stat-card">
                  <div className="fa-stat-value">{(stats.total_pages_analyzed || 0).toLocaleString()}</div>
                  <div className="fa-stat-label">Pages Analyzed</div>
                </div>
                <div className="fa-stat-card">
                  <div className="fa-stat-value">{(stats.total_products_extracted || 0).toLocaleString()}</div>
                  <div className="fa-stat-label">Products Extracted</div>
                </div>
                <div className="fa-stat-card">
                  <div className="fa-stat-value">{stats.unique_flyers || 0}</div>
                  <div className="fa-stat-label">Unique Flyers</div>
                </div>
                <div className="fa-stat-card">
                  <div className="fa-stat-value">{stats.avg_products_per_page?.toFixed(1) || '0'}</div>
                  <div className="fa-stat-label">Avg Products/Page</div>
                </div>
                <div className="fa-stat-card">
                  <div className="fa-stat-value" style={{ fontSize: '16px' }}>
                    {stats.date_range ? `${formatDate(stats.date_range.earliest)} – ${formatDate(stats.date_range.latest)}` : '—'}
                  </div>
                  <div className="fa-stat-label">Date Range</div>
                </div>
              </div>
            )}

            {/* Retailers Table */}
            {stats?.retailers?.length > 0 && (
              <div className="fa-table-wrapper">
                <h3 className="fa-table-title">Retailers</h3>
                <table className="fa-table">
                  <thead>
                    <tr>
                      <th>Retailer</th>
                      <th>Slug</th>
                      <th>Pages Analyzed</th>
                      <th>Flyer Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.retailers.map((r) => (
                      <tr
                        key={r.slug}
                        className="clickable"
                        onClick={() => navigate(`/flyer-analysis/browse?country=${country}&retailer=${r.slug}`)}
                      >
                        <td style={{ fontWeight: 500 }}>{r.retailer_name}</td>
                        <td style={{ color: '#6b7280' }}>{r.slug}</td>
                        <td>{r.pages}</td>
                        <td>{r.flyer_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Jobs Table */}
            <div className="fa-table-wrapper">
              <h3 className="fa-table-title">Recent Jobs</h3>
              {jobs.length === 0 ? (
                <div className="fa-empty">
                  <p>No analysis jobs yet</p>
                  <p className="fa-empty-subtitle">Create a new job to start analyzing flyers</p>
                </div>
              ) : (
                <table className="fa-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Cost</th>
                      <th>Created</th>
                      <th>Completed</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => {
                      const progress = job.progress || {};
                      const pct = progress.total_images > 0
                        ? Math.round((progress.processed / progress.total_images) * 100)
                        : 0;
                      return (
                        <tr key={job._id}>
                          <td>
                            <span className="fa-badge gray">
                              {job.type === 's3_import' ? 'S3 Import' : 'Upload'}
                            </span>
                          </td>
                          <td><span className={`fa-status ${job.status}`}>{job.status}</span></td>
                          <td>
                            <div className="fa-progress-wrapper">
                              <div className="fa-progress-track">
                                <div className="fa-progress-fill" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="fa-progress-text">
                                {progress.processed || 0}/{progress.total_images || 0}
                              </span>
                            </div>
                          </td>
                          <td style={{ color: '#6b7280' }}>
                            {job.estimated_cost_usd ? `$${job.estimated_cost_usd.toFixed(2)}` : '—'}
                          </td>
                          <td style={{ color: '#6b7280' }}>{formatRelativeTime(job.created_at)}</td>
                          <td style={{ color: '#6b7280' }}>{job.completed_at ? formatRelativeTime(job.completed_at) : '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              {job.status === 'paused' && (
                                <button className="fa-btn primary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => handleResumeJob(job._id)}>
                                  Resume
                                </button>
                              )}
                              {['queued', 'scanning', 'processing'].includes(job.status) && (
                                <button className="fa-btn outline" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => handlePauseJob(job._id)}>
                                  Pause
                                </button>
                              )}
                              {['queued', 'scanning', 'processing', 'paused'].includes(job.status) && (
                                <button className="fa-btn danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => handleCancelJob(job._id)}>
                                  Cancel
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
